import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  loadAllResourcePlanning,
  saveResourcePlanning,
} from "../services/azureResourcePlanningService";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import { addWeeks, getWeekDays } from "../utils/dateUtils";
import * as ProjectsService from "../services/azureProjectsService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { findTeamMember, getTeamMemberInitials } from "../utils/teamLookup";

// Standard-Aktivitaeten
const STANDARD_ACTIVITIES = [
  { code: "Einarbeiten", title: "Einarbeitung", color: "#6b7280" },
  { code: "Admin", title: "Administration", color: "#374151" },
  { code: "Akquise", title: "Akquise", color: "#059669" },
  { code: "Offerte", title: "Offerte", color: "#f59e0b" },
  { code: "Strategie", title: "Strategie", color: "#8b5cf6" },
  { code: "Ferien", title: "Ferien", color: "#6b7280" },
];

// ISO Kalenderwoche berechnen
const getISOWeekNumber = (date) => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const Ressourcenplanung = ({ readOnly = false }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [allResourceData, setAllResourceData] = useState({});
  const [projects, setProjects] = useState([]);
  const [expandedProject, setExpandedProject] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [manuallyAddedProjects, setManuallyAddedProjects] = useState([]);
  const [teamMembersData, setTeamMembersData] = useState([]);
  const [actualHoursData, setActualHoursData] = useState({});
  const [loadingActual, setLoadingActual] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startWeekOffset, setStartWeekOffset] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hoveredPersonWeek, setHoveredPersonWeek] = useState(null); // { personId, weekNumber, year }
  const [detailsProject, setDetailsProject] = useState(null); // Project showing detailed view

  const saveTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const currentWeekRef = useRef(null);
  const timelineWrapperRef = useRef(null);
  const projectRowRefs = useRef({});

  const VISIBLE_WEEKS = 52;
  const HOURS_PER_WEEK = 42;

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentWeek = getISOWeekNumber(today);

  // Wochen generieren
  const weeks = useMemo(() => {
    const result = [];
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + startWeekOffset * 7);

    for (let i = 0; i < VISIBLE_WEEKS; i++) {
      const weekDate = addWeeks(baseDate, i);
      const weekNum = getISOWeekNumber(weekDate);
      const year = weekDate.getFullYear();
      const weekDays = getWeekDays(weekDate);

      result.push({
        number: weekNum,
        year,
        startDate: weekDays[0],
        isCurrentWeek: weekNum === currentWeek && year === currentYear,
      });
    }
    return result;
  }, [startWeekOffset, currentWeek, currentYear]);

  // Get date range for current weeks view
  const getDateRangeForWeeks = useCallback(() => {
    if (weeks.length === 0) return { dateFrom: null, dateTo: null };

    const firstWeek = weeks[0];
    const lastWeek = weeks[weeks.length - 1];

    // Get start of first week
    const dateFrom = new Date(firstWeek.startDate);
    dateFrom.setDate(dateFrom.getDate() - dateFrom.getDay() + 1); // Monday

    // Get end of last week
    const dateTo = new Date(lastWeek.startDate);
    dateTo.setDate(dateTo.getDate() - dateTo.getDay() + 7); // Sunday

    const formatDate = (d) => d.toISOString().split('T')[0];

    return {
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
  }, [weeks]);

  // Aggregate timekeeping data by week/project/employee
  const aggregateTimekeepingData = useCallback((timekeepingEntries) => {
    const aggregated = {}; // { projectCode: { KWxx_year: { employeeUuid: hours } } }

    // Get standard activity codes for matching TypeName
    const standardActivityCodes = STANDARD_ACTIVITIES.map(a => a.code);

    timekeepingEntries.forEach((entry) => {
      if (!entry.date) return;

      // Calculate ISO week from date
      const date = new Date(entry.date);
      const weekNum = getISOWeekNumber(date);
      const year = date.getFullYear();
      const weekKey = `KW${weekNum}_${year}`;

      const employeeId = entry.employeeUuid;
      const hours = entry.durationHours + (entry.durationMinutes / 60);

      // Helper function to add hours to aggregated data
      const addHours = (code) => {
        if (!code) return;
        if (!aggregated[code]) {
          aggregated[code] = {};
        }
        if (!aggregated[code][weekKey]) {
          aggregated[code][weekKey] = {};
        }
        if (!aggregated[code][weekKey][employeeId]) {
          aggregated[code][weekKey][employeeId] = 0;
        }
        aggregated[code][weekKey][employeeId] += hours;
      };

      // Aggregate by project code if available
      if (entry.code) {
        addHours(entry.code);
      }

      // Also aggregate by TypeName if it matches a standard activity (except "Arbeit" which is generic)
      if (entry.typeName && entry.typeName !== "Arbeit" && standardActivityCodes.includes(entry.typeName)) {
        addHours(entry.typeName);
      }
    });

    return aggregated;
  }, []);

  // Load actual hours from timekeeping for the current week range
  const loadActualHours = useCallback(async () => {
    const { dateFrom, dateTo } = getDateRangeForWeeks();
    if (!dateFrom || !dateTo) return;

    setLoadingActual(true);
    try {
      const timekeepingData = await getTimekeepingData(dateFrom, dateTo);
      const aggregated = aggregateTimekeepingData(timekeepingData);
      setActualHoursData(aggregated);
    } catch (err) {
      console.error("Error loading actual hours:", err);
    } finally {
      setLoadingActual(false);
    }
  }, [getDateRangeForWeeks, aggregateTimekeepingData]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    document.body.classList.add("wide-page");
    return () => document.body.classList.remove("wide-page");
  }, []);

  // Close expanded project when clicking outside timeline-wrapper
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        expandedProject &&
        timelineWrapperRef.current &&
        !timelineWrapperRef.current.contains(event.target)
      ) {
        setExpandedProject(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expandedProject]);

  // Load actual hours when weeks change (after initial data is loaded)
  useEffect(() => {
    if (!loading) {
      loadActualHours();
    }
  }, [loading, startWeekOffset, loadActualHours]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resourceData, projectsData, teamMembers] = await Promise.all([
        loadAllResourcePlanning(),
        ProjectsService.getAllProjects(),
        getAllTeamMembers(),
      ]);

      // Filter team members: show those who are active or have future start dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activeTeamMembers = teamMembers.filter((tm) => {
        if (!tm.employeeUuid) return false;

        // Ende must not be in the past (empty Ende means no end date)
        if (tm.ende) {
          const endeDate = new Date(tm.ende);
          if (endeDate < today) return false;
        }

        // Include if imTeam=true
        if (tm.imTeam) return true;

        // Also include if has a future Start date (for advance planning)
        if (tm.start) {
          const startDate = new Date(tm.start);
          if (startDate > today) return true;
        }

        return false;
      });

      setEmployees(activeTeamMembers);
      setSelectedPersons(activeTeamMembers.map((tm) => tm.employeeUuid));
      setAllResourceData(resourceData);
      setProjects(projectsData);
      setTeamMembersData(teamMembers);
    } catch (err) {
      console.error("Fehler beim Laden:", err);
      setError("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  // Alle Projekte und Offerten aus DB
  const availableProjects = useMemo(() => {
    // Projekte und Offerten (type = 'project' oder 'offerte')
    const dbProjects = projects
      .filter((p) => p.type === "project" || p.type === "offerte" || !p.type)
      .map((p) => ({
        code: p.code,
        title: p.title,
        color: p.color || "#059669",
        type: p.type || "project",
      }));

    return [...STANDARD_ACTIVITIES, ...dbProjects];
  }, [projects]);

  // Projekte mit Daten
  const projectsWithData = useMemo(() => {
    const projectCodes = new Set();

    // Include manually added projects
    manuallyAddedProjects.forEach((code) => projectCodes.add(code));

    weeks.forEach((week) => {
      const weekKey = `KW${week.number}_${week.year}`;

      Object.keys(allResourceData).forEach((projectCode) => {
        const weekData = allResourceData[projectCode]?.[weekKey];
        if (weekData) {
          const hasHours = Object.entries(weekData).some(
            ([uuid, hours]) => selectedPersons.includes(uuid) && hours > 0
          );
          if (hasHours) {
            projectCodes.add(projectCode);
          }
        }
      });
    });

    const standardCodes = STANDARD_ACTIVITIES.map((a) => a.code);
    return Array.from(projectCodes).sort((a, b) => {
      const aIsStandard = standardCodes.includes(a);
      const bIsStandard = standardCodes.includes(b);
      if (aIsStandard && !bIsStandard) return -1;
      if (!aIsStandard && bIsStandard) return 1;
      return a.localeCompare(b);
    });
  }, [allResourceData, weeks, selectedPersons, manuallyAddedProjects]);

  // Stunden fuer Projekt/Woche (alle ausgewaehlten Personen)
  const getProjectHoursForWeek = useCallback(
    (projectCode, weekNumber, year) => {
      const weekKey = `KW${weekNumber}_${year}`;
      const weekData = allResourceData[projectCode]?.[weekKey];
      if (!weekData) return 0;

      return Object.entries(weekData)
        .filter(([uuid]) => selectedPersons.includes(uuid))
        .reduce((sum, [, hours]) => sum + (hours || 0), 0);
    },
    [allResourceData, selectedPersons]
  );

  // Stunden fuer Projekt/Woche/Person
  const getPersonHoursForWeek = useCallback(
    (projectCode, weekNumber, year, personUuid) => {
      const weekKey = `KW${weekNumber}_${year}`;
      return allResourceData[projectCode]?.[weekKey]?.[personUuid] || 0;
    },
    [allResourceData]
  );

  // Total Stunden fuer Woche
  const getTotalHoursForWeek = useCallback(
    (weekNumber, year) => {
      let total = 0;
      const weekKey = `KW${weekNumber}_${year}`;

      Object.keys(allResourceData).forEach((projectCode) => {
        const weekData = allResourceData[projectCode]?.[weekKey];
        if (weekData) {
          Object.entries(weekData)
            .filter(([uuid]) => selectedPersons.includes(uuid))
            .forEach(([, hours]) => {
              total += hours || 0;
            });
        }
      });

      return total;
    },
    [allResourceData, selectedPersons]
  );

  // Actual hours for project/week (from timekeeping)
  const getActualProjectHoursForWeek = useCallback(
    (projectCode, weekNumber, year) => {
      const weekKey = `KW${weekNumber}_${year}`;
      const weekData = actualHoursData[projectCode]?.[weekKey];
      if (!weekData) return 0;

      return Object.entries(weekData)
        .filter(([uuid]) => selectedPersons.includes(uuid))
        .reduce((sum, [, hours]) => sum + (hours || 0), 0);
    },
    [actualHoursData, selectedPersons]
  );

  // Actual hours for project/week/person (from timekeeping)
  const getActualPersonHoursForWeek = useCallback(
    (projectCode, weekNumber, year, personUuid) => {
      const weekKey = `KW${weekNumber}_${year}`;
      return actualHoursData[projectCode]?.[weekKey]?.[personUuid] || 0;
    },
    [actualHoursData]
  );

  // Total actual hours for week (from timekeeping)
  const getTotalActualHoursForWeek = useCallback(
    (weekNumber, year) => {
      let total = 0;
      const weekKey = `KW${weekNumber}_${year}`;

      Object.keys(actualHoursData).forEach((projectCode) => {
        const weekData = actualHoursData[projectCode]?.[weekKey];
        if (weekData) {
          Object.entries(weekData)
            .filter(([uuid]) => selectedPersons.includes(uuid))
            .forEach(([, hours]) => {
              total += hours || 0;
            });
        }
      });

      return total;
    },
    [actualHoursData, selectedPersons]
  );

  // Check if week is in the past (for showing actual hours)
  const isWeekInPast = useCallback(
    (weekNumber, year) => {
      if (year < currentYear) return true;
      if (year > currentYear) return false;
      return weekNumber < currentWeek;
    },
    [currentWeek, currentYear]
  );

  // Calculate available capacity for a week based on active team members
  const getWeekCapacity = useCallback(
    (weekStartDate) => {
      let totalCapacity = 0;

      teamMembersData.forEach((tm) => {
        const personId = tm.employeeUuid;
        if (!personId || !selectedPersons.includes(personId)) return;

        // Check if team member is active during this week
        const weekStart = new Date(weekStartDate);
        const weekEnd = new Date(weekStartDate);
        weekEnd.setDate(weekEnd.getDate() + 6); // End of week (Sunday)

        // Check Start date - member must have started by end of this week
        if (tm.start) {
          const startDate = new Date(tm.start);
          if (startDate > weekEnd) return; // Hasn't started yet
        }

        // Check Ende date - member must not have ended before this week starts
        if (tm.ende) {
          const endeDate = new Date(tm.ende);
          if (endeDate < weekStart) return; // Already ended
        }

        // Add this member's capacity (pensum percentage of 42 hours)
        const pensum = tm.pensum ?? 100;
        totalCapacity += (pensum / 100) * HOURS_PER_WEEK;
      });

      return totalCapacity;
    },
    [teamMembersData, selectedPersons]
  );

  // Segmente fuer gestackte Balken
  const getProjectSegmentsForWeek = useCallback(
    (weekNumber, year) => {
      const segments = [];

      projectsWithData.forEach((projectCode) => {
        const hours = getProjectHoursForWeek(projectCode, weekNumber, year);
        if (hours > 0) {
          const project = availableProjects.find((p) => p.code === projectCode);
          segments.push({
            projectCode,
            hours,
            color: project?.color || "#6b7280",
          });
        }
      });

      return segments;
    },
    [projectsWithData, getProjectHoursForWeek, availableProjects]
  );

  const totalCapacity = selectedPersons.length * HOURS_PER_WEEK;

  const getTeamMemberId = useCallback((tm) => tm.employeeUuid, []);

  // Get pensum for a person by identifier (default 100%)
  const getPersonPensum = useCallback(
    (personId) => {
      const teamMember = findTeamMember(teamMembersData, personId);
      return teamMember?.pensum ?? 100;
    },
    [teamMembersData]
  );

  // Get max hours per week for a person based on pensum
  const getPersonMaxHours = useCallback(
    (personId) => {
      const pensum = getPersonPensum(personId);
      return (pensum / 100) * HOURS_PER_WEEK;
    },
    [getPersonPensum]
  );

  // Get total hours for a person in a week across ALL projects
  const getPersonTotalHoursForWeek = useCallback(
    (personId, weekNumber, year) => {
      const weekKey = `KW${weekNumber}_${year}`;
      let total = 0;

      Object.keys(allResourceData).forEach((projectCode) => {
        const hours = allResourceData[projectCode]?.[weekKey]?.[personId] || 0;
        total += hours;
      });

      return total;
    },
    [allResourceData]
  );

  // Check if person is over capacity for a week
  const isPersonOverCapacity = useCallback(
    (personId, weekNumber, year) => {
      const totalHours = getPersonTotalHoursForWeek(personId, weekNumber, year);
      const maxHours = getPersonMaxHours(personId);
      return totalHours > maxHours;
    },
    [getPersonTotalHoursForWeek, getPersonMaxHours]
  );

  const getProjectColor = (projectCode) => {
    const project = availableProjects.find((p) => p.code === projectCode);
    return project?.color || "#6b7280";
  };

  const getProjectTitle = (projectCode) => {
    const project = availableProjects.find((p) => p.code === projectCode);
    return project?.title || projectCode;
  };

  const handleNavigate = (direction) => {
    setStartWeekOffset((prev) => prev + direction * 4);
  };

  const handleToday = () => {
    setStartWeekOffset(0);
    setTimeout(() => {
      currentWeekRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }, 100);
  };

  const handlePersonToggle = (personId) => {
    setSelectedPersons((prev) => {
      if (prev.includes(personId)) {
        return prev.filter((id) => id !== personId);
      }
      return [...prev, personId];
    });
  };

  const handleSelectAll = () => {
    setSelectedPersons(employees.map((e) => e.employeeUuid));
  };

  const handleSelectNone = () => {
    setSelectedPersons([]);
  };

  const handleProjectClick = (projectCode) => {
    setExpandedProject((prev) => {
      const newExpanded = prev === projectCode ? null : projectCode;
      // Reset details view when switching projects or collapsing
      if (newExpanded !== prev) {
        setDetailsProject(null);
      }
      return newExpanded;
    });
  };

  // Stunden fuer eine Person in einer Woche setzen
  const handlePersonHoursChange = (
    projectCode,
    weekNumber,
    year,
    personUuid,
    value
  ) => {
    const hours = parseFloat(value) || 0;
    const weekKey = `KW${weekNumber}_${year}`;

    setAllResourceData((prev) => {
      const newData = { ...prev };
      if (!newData[projectCode]) {
        newData[projectCode] = {};
      }
      if (!newData[projectCode][weekKey]) {
        newData[projectCode][weekKey] = {};
      }
      newData[projectCode][weekKey][personUuid] = hours;
      return newData;
    });

    // Debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const currentProjectData = allResourceData[projectCode] || {};
        const updatedWeekData = {
          ...currentProjectData[weekKey],
          [personUuid]: hours,
        };

        await saveResourcePlanning(projectCode, {
          ...currentProjectData,
          [weekKey]: updatedWeekData,
        });
      } catch (err) {
        console.error("Speichern fehlgeschlagen:", err);
        setError("Fehler beim Speichern");
      }
    }, 500);
  };

  const formatWeekDate = (date) => {
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const MAX_BAR_HEIGHT = 200;
  const getBarHeight = (hours) => {
    if (totalCapacity === 0) return 0;
    return Math.min(
      (hours / totalCapacity) * MAX_BAR_HEIGHT,
      MAX_BAR_HEIGHT * 1.5
    );
  };

  // Get team member name by identifier (employeeUuid, nummer oder teamId)
  const getEmployeeName = (personId) => {
    const member = findTeamMember(employees, personId);
    if (!member) {
      if (personId) {
        console.warn(
          "[Ressourcenplanung] Lookup fehlgeschlagen für personId:",
          personId,
          "verfügbare Schlüssel:",
          employees.length
        );
      }
      return "Unbekannt (gelöscht)";
    }
    return member.name || "Unbekannt (gelöscht)";
  };

  // Get team member initials by identifier
  const getEmployeeInitials = (personId) =>
    getTeamMemberInitials(employees, personId);

  // Get consistent color for a person based on their index
  const PERSON_COLORS = [
    "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
    "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
  ];
  const getPersonColor = useCallback((personId) => {
    const idx = selectedPersons.indexOf(personId);
    return PERSON_COLORS[idx % PERSON_COLORS.length];
  }, [selectedPersons]);

  if (loading) {
    return (
      <div className="ressourcenplanung">
        <div className="container">
          <div className="loading-state">Lade Daten...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ressourcenplanung">
      <div className="container">
        <h1>Ressourcenplanung</h1>

        {error && <div className="error-message">{error}</div>}

        {/* Filter-Leiste */}
        <div className="filter-bar">
          <div className="person-filter">
            <button
              className="filter-button"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              Personen ({selectedPersons.length}/{employees.length})
            </button>

            {filterOpen && (
              <div className="filter-dropdown">
                <div className="filter-actions">
                  <button onClick={handleSelectAll}>Alle</button>
                  <button onClick={handleSelectNone}>Keine</button>
                </div>
                <div className="filter-options">
                  {employees.map((emp) => (
                    <label
                      key={emp.employeeUuid}
                      className="filter-option"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPersons.includes(emp.employeeUuid)}
                        onChange={() => handlePersonToggle(emp.employeeUuid)}
                      />
                      <span>{emp.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-controls">
            <button className="nav-btn" onClick={() => handleNavigate(-1)}>
              Zurück
            </button>
            <button className="today-btn" onClick={handleToday}>
              Heute
            </button>
            <button className="nav-btn" onClick={() => handleNavigate(1)}>
              Vor
            </button>
          </div>

          <div className="capacity-info">{totalCapacity}h/Woche</div>
        </div>

        {/* Timeline */}
        <div className="timeline-wrapper" ref={timelineWrapperRef}>
          <div className="timeline-content">
            {/* Total-Zeile */}

            {/* KW Header - unter den Balken */}
            <div className="timeline-header">
              <div className="label-column header-label"></div>
              {weeks.map((week, idx) => {
                const isNewYear = idx > 0 && weeks[idx - 1].year !== week.year;
                return (
                  <div
                    key={`${week.number}-${week.year}`}
                    className={`week-column ${
                      week.isCurrentWeek ? "current-week" : ""
                    }`}
                    ref={week.isCurrentWeek ? currentWeekRef : null}
                  >
                    {(idx === 0 || isNewYear) && (
                      <div className="year-label">{week.year}</div>
                    )}
                    <div className="week-number">{week.number}</div>
                    <div className="week-date">
                      {formatWeekDate(week.startDate)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Build total actual hours points and capacity line */}
            {(() => {
              const totalActualPoints = [];
              const capacityPoints = [];

              // Find max value for scaling (max of capacity, planned hours, and actual hours)
              let maxValue = 0;
              weeks.forEach((week) => {
                const weekCapacity = getWeekCapacity(week.startDate);
                const plannedHours = getTotalHoursForWeek(week.number, week.year);
                const isPast = isWeekInPast(week.number, week.year);
                const actualHours = isPast ? getTotalActualHoursForWeek(week.number, week.year) : 0;
                maxValue = Math.max(maxValue, weekCapacity, plannedHours, actualHours);
              });

              // Build points for both lines
              weeks.forEach((week, idx) => {
                const isPast = isWeekInPast(week.number, week.year);

                // Actual hours (past weeks only)
                if (isPast) {
                  const totalActualHours = getTotalActualHoursForWeek(week.number, week.year);
                  if (totalActualHours > 0) {
                    const height = maxValue > 0
                      ? (totalActualHours / maxValue) * MAX_BAR_HEIGHT
                      : 0;
                    totalActualPoints.push({
                      idx,
                      x: idx * 36 + 18,
                      y: MAX_BAR_HEIGHT - height,
                      hours: totalActualHours,
                    });
                  }
                }

                // Capacity line - horizontal segment for each week
                const weekCapacity = getWeekCapacity(week.startDate);
                const capacityHeight = maxValue > 0
                  ? (weekCapacity / maxValue) * MAX_BAR_HEIGHT
                  : 0;
                capacityPoints.push({
                  idx,
                  xStart: idx * 36,
                  xEnd: (idx + 1) * 36,
                  y: MAX_BAR_HEIGHT - capacityHeight,
                  capacity: weekCapacity,
                });
              });

              const totalPolylinePoints = totalActualPoints
                .map((p) => `${p.x},${p.y}`)
                .join(" ");

              // Build capacity polyline with horizontal segments
              const capacityPolylinePoints = capacityPoints
                .flatMap((p, idx) => {
                  const points = [`${p.xStart},${p.y}`, `${p.xEnd},${p.y}`];
                  return points;
                })
                .join(" ");

              // Helper to get bar height using max value
              const getBarHeightDynamic = (hours) => {
                if (maxValue === 0) return 0;
                return (hours / maxValue) * MAX_BAR_HEIGHT;
              };

              return (
                <div className="total-row">
                  <div className="label-column total-label">Total</div>

                  {/* SVG overlay for capacity and actual hours lines */}
                  <svg
                    className="total-lines-overlay"
                    style={{
                      position: "absolute",
                      left: "140px",
                      bottom: "22px",
                      width: `${weeks.length * 36}px`,
                      height: `${MAX_BAR_HEIGHT}px`,
                      pointerEvents: "none",
                      zIndex: 15,
                    }}
                  >
                    {/* Capacity line (horizontal segments) */}
                    <polyline
                      points={capacityPolylinePoints}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.5)"
                      strokeWidth="2"
                      strokeDasharray="4 4"
                    />

                    {/* Actual hours line */}
                    {totalActualPoints.length > 1 && (
                      <>
                        <polyline
                          points={totalPolylinePoints}
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                        />
                        {totalActualPoints.map((p) => (
                          <circle
                            key={p.idx}
                            cx={p.x}
                            cy={p.y}
                            r="5"
                            fill="white"
                            stroke="white"
                            strokeWidth="2"
                          />
                        ))}
                      </>
                    )}
                  </svg>

                  {weeks.map((week) => {
                    const segments = getProjectSegmentsForWeek(
                      week.number,
                      week.year
                    );
                    const totalHours = getTotalHoursForWeek(week.number, week.year);
                    const weekCapacity = getWeekCapacity(week.startDate);
                    const isOverCapacity = totalHours > weekCapacity;
                    const isPast = isWeekInPast(week.number, week.year);

                    return (
                      <div
                        key={`total-${week.number}-${week.year}`}
                        className={`week-column total-cell ${
                          isOverCapacity ? "over-capacity" : ""
                        } ${isPast ? "past-week" : ""}`}
                      >
                        <div className="stacked-bar-container">
                          <div className="stacked-bars">
                            {segments.map((segment) => {
                              const isHighlighted =
                                hoveredProject === segment.projectCode ||
                                expandedProject === segment.projectCode;
                              const isDimmed =
                                (hoveredProject && hoveredProject !== segment.projectCode) ||
                                (expandedProject && expandedProject !== segment.projectCode);
                              return (
                                <div
                                  key={segment.projectCode}
                                  className={`bar-segment ${isHighlighted ? "highlighted" : ""} ${isDimmed ? "dimmed" : ""}`}
                                  style={{
                                    height: `${getBarHeightDynamic(segment.hours)}px`,
                                    backgroundColor: segment.color,
                                  }}
                                  onMouseEnter={() =>
                                    setHoveredProject(segment.projectCode)
                                  }
                                  onMouseLeave={() => setHoveredProject(null)}
                                  onClick={() => {
                                    setExpandedProject(segment.projectCode);
                                    // Scroll to the project row
                                    setTimeout(() => {
                                      const projectRow = projectRowRefs.current[segment.projectCode];
                                      if (projectRow) {
                                        projectRow.scrollIntoView({
                                          behavior: "smooth",
                                          block: "center",
                                        });
                                      }
                                    }, 50);
                                  }}
                                  title={`${segment.projectCode}: ${segment.hours}h`}
                                />
                              );
                            })}
                          </div>
                        </div>
                        <div className="total-hours">
                          {totalHours > 0 && `${Math.round(totalHours)}h`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Trennlinie */}
            <div className="separator-row" />

            {/* Projekt-Zeilen */}
            {projectsWithData.map((projectCode) => {
              const isExpanded = expandedProject === projectCode;
              const isHovered = hoveredProject === projectCode;
              const isDimmed = (expandedProject && !isExpanded) || (hoveredProject && !isHovered);
              const color = getProjectColor(projectCode);

              // First pass: find max hours for this project (both planned and actual)
              let maxProjectHours = 0;
              weeks.forEach((week) => {
                const plannedHours = getProjectHoursForWeek(projectCode, week.number, week.year);
                const isPast = isWeekInPast(week.number, week.year);
                const actualHours = isPast
                  ? getActualProjectHoursForWeek(projectCode, week.number, week.year)
                  : 0;
                maxProjectHours = Math.max(maxProjectHours, plannedHours, actualHours);
              });

              // Dynamic height calculator for this project row
              const PROJECT_ROW_HEIGHT = 100; // Available height for bars
              const getProjectBarHeight = (hours) => {
                if (maxProjectHours === 0) return 0;
                return Math.max((hours / maxProjectHours) * PROJECT_ROW_HEIGHT, hours > 0 ? 8 : 0);
              };

              // Build actual hours points for the entire row using dynamic scaling
              const actualHoursPoints = [];
              weeks.forEach((week, idx) => {
                const isPast = isWeekInPast(week.number, week.year);
                if (isPast) {
                  const actualHours = getActualProjectHoursForWeek(
                    projectCode,
                    week.number,
                    week.year
                  );
                  if (actualHours > 0) {
                    const height = getProjectBarHeight(actualHours);
                    actualHoursPoints.push({
                      idx,
                      x: idx * 36 + 18, // center of column
                      y: PROJECT_ROW_HEIGHT - height + 10, // +10 for padding
                      hours: actualHours,
                      height,
                    });
                  }
                }
              });

              // Create polyline points string
              const polylinePoints = actualHoursPoints
                .map((p) => `${p.x},${p.y}`)
                .join(" ");

              return (
                <React.Fragment key={projectCode}>
                  <div
                    ref={(el) => (projectRowRefs.current[projectCode] = el)}
                    className={`project-row ${isHovered || isExpanded ? "highlighted" : ""} ${isDimmed ? "dimmed" : ""}`}
                    onClick={() => handleProjectClick(projectCode)}
                  >
                    <div className="label-column project-label">
                      <span
                        className="color-dot"
                        style={{ backgroundColor: color }}
                      />
                      <span className="project-code">{projectCode}</span>
                      {getProjectTitle(projectCode) !== projectCode && (
                        <span className="project-title">
                          {getProjectTitle(projectCode)}
                        </span>
                      )}
                    </div>

                    {/* SVG overlay for actual hours line - hidden when details view is active */}
                    {actualHoursPoints.length > 1 && detailsProject !== projectCode && (
                      <svg
                        className="actual-hours-overlay"
                        style={{
                          position: "absolute",
                          left: "140px",
                          bottom: "4px",
                          width: `${weeks.length * 36}px`,
                          height: `${PROJECT_ROW_HEIGHT + 10}px`,
                          pointerEvents: "none",
                          zIndex: 15,
                        }}
                      >
                        <polyline
                          points={polylinePoints}
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                        />
                        {actualHoursPoints.map((p) => (
                          <circle
                            key={p.idx}
                            cx={p.x}
                            cy={p.y}
                            r="4"
                            fill="white"
                            stroke="white"
                            strokeWidth="2"
                          />
                        ))}
                      </svg>
                    )}

                    {weeks.map((week, idx) => {
                      const hours = getProjectHoursForWeek(
                        projectCode,
                        week.number,
                        week.year
                      );
                      const prevHours =
                        idx > 0
                          ? getProjectHoursForWeek(
                              projectCode,
                              weeks[idx - 1].number,
                              weeks[idx - 1].year
                            )
                          : 0;
                      const nextHours =
                        idx < weeks.length - 1
                          ? getProjectHoursForWeek(
                              projectCode,
                              weeks[idx + 1].number,
                              weeks[idx + 1].year
                            )
                          : 0;
                      const showLine =
                        hours > 0 || prevHours > 0 || nextHours > 0;
                      const barHeight = getProjectBarHeight(hours);

                      // Actual hours from timekeeping (past weeks only)
                      const isPast = isWeekInPast(week.number, week.year);
                      const actualHours = isPast
                        ? getActualProjectHoursForWeek(
                            projectCode,
                            week.number,
                            week.year
                          )
                        : 0;

                      return (
                        <div
                          key={`${projectCode}-${week.number}-${week.year}`}
                          className={`week-column project-cell ${
                            showLine ? "has-activity" : ""
                          } ${isPast ? "past-week" : ""}`}
                        >
                          {hours > 0 && (
                            <div
                              className="project-bar"
                              style={{
                                height: `${barHeight}px`,
                                backgroundColor: color,
                              }}
                            >
                              <span className="bar-hours">
                                {Math.round(hours)}
                              </span>
                            </div>
                          )}
                          {/* Show single dot if only one point - hidden when details view is active */}
                          {isPast && actualHours > 0 && actualHoursPoints.length === 1 && detailsProject !== projectCode && (
                            <div
                              className="actual-hours-dot"
                              style={{ bottom: `${getProjectBarHeight(actualHours)}px` }}
                              title={`Ist: ${Math.round(actualHours)}h`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Personen-Zeilen wenn expanded */}
                  {isExpanded &&
                    selectedPersons.map((personUuid) => {
                      // Calculate remaining hours for hovered week
                      const isHoveredPerson = hoveredPersonWeek?.personId === personUuid;
                      const remainingHours = isHoveredPerson
                        ? getPersonMaxHours(personUuid) -
                          getPersonTotalHoursForWeek(
                            personUuid,
                            hoveredPersonWeek.weekNumber,
                            hoveredPersonWeek.year
                          )
                        : null;

                      return (
                        <div
                          key={`${projectCode}-${personUuid}`}
                          className="person-row"
                        >
                          <div className="label-column person-label">
                            <span className="person-name">{getEmployeeName(personUuid)}</span>
                            {isHoveredPerson && remainingHours !== null && (
                              <span className="remaining-hours">
                                {remainingHours >= 0 ? `+${Math.round(remainingHours)}h` : `${Math.round(remainingHours)}h`}
                              </span>
                            )}
                          </div>
                          {weeks.map((week) => {
                            const hours = getPersonHoursForWeek(
                              projectCode,
                              week.number,
                              week.year,
                              personUuid
                            );
                            const overCapacity = isPersonOverCapacity(
                              personUuid,
                              week.number,
                              week.year
                            );
                            return (
                              <div
                                key={`${projectCode}-${personUuid}-${week.number}-${week.year}`}
                                className={`week-column person-cell ${
                                  overCapacity ? "over-capacity" : ""
                                }`}
                              >
                                <input
                                  type="number"
                                  min="0"
                                  max="60"
                                  step="0.5"
                                  value={hours || ""}
                                  placeholder=""
                                  disabled={readOnly}
                                  onChange={(e) =>
                                    handlePersonHoursChange(
                                      projectCode,
                                      week.number,
                                      week.year,
                                      personUuid,
                                      e.target.value
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.target.select()}
                                  onMouseEnter={() =>
                                    setHoveredPersonWeek({
                                      personId: personUuid,
                                      weekNumber: week.number,
                                      year: week.year,
                                    })
                                  }
                                  onMouseLeave={() => setHoveredPersonWeek(null)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                  {/* Details button row */}
                  {isExpanded && (
                    <div
                      className="details-toggle-row"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailsProject(detailsProject === projectCode ? null : projectCode);
                      }}
                    >
                      <div className="label-column details-label">
                        {detailsProject === projectCode ? "▼ Details" : "▶ Details"}
                      </div>
                      {detailsProject !== projectCode && (
                        <div className="details-hint">Klicken für Detailansicht</div>
                      )}
                    </div>
                  )}

                  {/* Details view - stacked bar charts */}
                  {isExpanded && detailsProject === projectCode && (() => {
                    // Calculate max hours for scaling in details view
                    let detailsMaxHours = 0;
                    weeks.forEach((week) => {
                      const isPast = isWeekInPast(week.number, week.year);
                      let weekPlannedTotal = 0;
                      let weekActualTotal = 0;
                      selectedPersons.forEach((personId) => {
                        weekPlannedTotal += getPersonHoursForWeek(projectCode, week.number, week.year, personId);
                        if (isPast) {
                          weekActualTotal += getActualPersonHoursForWeek(projectCode, week.number, week.year, personId);
                        }
                      });
                      detailsMaxHours = Math.max(detailsMaxHours, weekPlannedTotal, weekActualTotal);
                    });

                    const DETAILS_BAR_HEIGHT = 140;
                    const getDetailsBarHeight = (hours) => {
                      if (detailsMaxHours === 0) return 0;
                      return (hours / detailsMaxHours) * DETAILS_BAR_HEIGHT;
                    };

                    return (
                      <div className="details-row">
                        <div className="label-column details-legend">
                          {selectedPersons.map((personId) => (
                            <div key={personId} className="legend-person">
                              <span
                                className="legend-color"
                                style={{ backgroundColor: getPersonColor(personId) }}
                              />
                              <span className="legend-initials">{getEmployeeInitials(personId)}</span>
                            </div>
                          ))}
                        </div>
                        {weeks.map((week) => {
                          const isPast = isWeekInPast(week.number, week.year);

                          // Build stacked segments for planned hours
                          const plannedSegments = selectedPersons
                            .map((personId) => ({
                              personId,
                              hours: getPersonHoursForWeek(projectCode, week.number, week.year, personId),
                              color: getPersonColor(personId),
                            }))
                            .filter((s) => s.hours > 0);

                          // Build stacked segments for actual hours (past only)
                          const actualSegments = isPast
                            ? selectedPersons
                                .map((personId) => ({
                                  personId,
                                  hours: getActualPersonHoursForWeek(projectCode, week.number, week.year, personId),
                                  color: getPersonColor(personId),
                                }))
                                .filter((s) => s.hours > 0)
                            : [];

                          return (
                            <div
                              key={`details-${week.number}-${week.year}`}
                              className={`week-column details-cell ${isPast ? "past-week" : ""}`}
                            >
                              <div className="details-bars-container">
                                {/* Labels above bars - only for past weeks */}
                                {isPast && (
                                  <div className="details-bar-labels">
                                    <span className="bar-label">P</span>
                                    <span className="bar-label">I</span>
                                  </div>
                                )}
                                <div className="details-bars-wrapper">
                                  {/* For past weeks: show two bars side by side */}
                                  {isPast ? (
                                    <>
                                      <div className="details-bar-wrapper planned">
                                        <div className="stacked-bar">
                                          {plannedSegments.map((seg) => (
                                            <div
                                              key={`planned-${seg.personId}`}
                                              className="bar-segment"
                                              style={{
                                                height: `${getDetailsBarHeight(seg.hours)}px`,
                                                backgroundColor: seg.color,
                                                opacity: 0.5,
                                              }}
                                              title={`Plan: ${getEmployeeName(seg.personId)} - ${Math.round(seg.hours)}h`}
                                            />
                                          ))}
                                        </div>
                                      </div>
                                      <div className="details-bar-wrapper actual">
                                        <div className="stacked-bar">
                                          {actualSegments.map((seg) => (
                                            <div
                                              key={`actual-${seg.personId}`}
                                              className="bar-segment"
                                              style={{
                                                height: `${getDetailsBarHeight(seg.hours)}px`,
                                                backgroundColor: seg.color,
                                              }}
                                              title={`Ist: ${getEmployeeName(seg.personId)} - ${Math.round(seg.hours)}h`}
                                            />
                                          ))}
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    /* For future weeks: show single stacked bar */
                                    <div className="details-bar-wrapper future">
                                      <div className="stacked-bar">
                                        {plannedSegments.map((seg) => (
                                          <div
                                            key={`future-${seg.personId}`}
                                            className="bar-segment"
                                            style={{
                                              height: `${getDetailsBarHeight(seg.hours)}px`,
                                              backgroundColor: seg.color,
                                            }}
                                            title={`${getEmployeeName(seg.personId)} - ${Math.round(seg.hours)}h`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </React.Fragment>
              );
            })}

            {/* Projekt hinzufügen */}
            <div className="add-project-row">
              <div className="label-column">
                <select
                  className="project-select"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      const code = e.target.value;
                      // Add to manually added projects so it appears in the list
                      setManuallyAddedProjects((prev) =>
                        prev.includes(code) ? prev : [...prev, code]
                      );
                      // Initialisiere leere Daten wenn nicht vorhanden
                      if (!allResourceData[code]) {
                        setAllResourceData((prev) => ({
                          ...prev,
                          [code]: {},
                        }));
                      }
                      setExpandedProject(code);
                    }
                  }}
                >
                  <option value="">+ Projekt hinzufügen</option>
                  <optgroup label="Aktivitaeten">
                    {STANDARD_ACTIVITIES.filter(
                      (a) => !projectsWithData.includes(a.code)
                    ).map((activity) => (
                      <option key={activity.code} value={activity.code}>
                        {activity.code} - {activity.title}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Projekte">
                    {projects
                      .filter(
                        (p) =>
                          (p.type === "project" || !p.type) &&
                          !projectsWithData.includes(p.code)
                      )
                      .map((project) => (
                        <option key={project.code} value={project.code}>
                          {project.code} - {project.title}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Offerten">
                    {projects
                      .filter(
                        (p) =>
                          p.type === "offerte" &&
                          !projectsWithData.includes(p.code)
                      )
                      .map((project) => (
                        <option key={project.code} value={project.code}>
                          {project.code} - {project.title}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Legende */}
        <div className="legend">
          <div className="legend-items">
            {projectsWithData.map((projectCode) => (
              <div
                key={projectCode}
                className={`legend-item ${
                  hoveredProject === projectCode ? "highlighted" : ""
                }`}
                onMouseEnter={() => setHoveredProject(projectCode)}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <span
                  className="legend-color"
                  style={{ backgroundColor: getProjectColor(projectCode) }}
                />
                <span className="legend-code">{projectCode}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ressourcenplanung;
